import { ErrorHandler, Injectable } from '@angular/core';
import { ErrorLoggingService } from './error-logging.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private lastError: string | null = null;
  private lastErrorTime: number = 0;
  private readonly ERROR_COOLDOWN_MS = 1000; // Ignore duplicate errors within 1 second

  constructor(private errorLogging: ErrorLoggingService) {}

  handleError(error: unknown): void {
    const errorSignature = this.getErrorSignature(error);
    const now = Date.now();

    // Check if this is a duplicate error within the cooldown period
    if (this.lastError === errorSignature && (now - this.lastErrorTime) < this.ERROR_COOLDOWN_MS) {
      // Skip logging duplicate error
      return;
    }

    // Update last error tracking
    this.lastError = errorSignature;
    this.lastErrorTime = now;

    // Log the error
    this.errorLogging.logError(error, 'angular');
    console.error(error);
  }

  /**
   * Generate a unique signature for an error to identify duplicates
   */
  private getErrorSignature(error: unknown): string {
    if (error instanceof Error) {
      // Use error name, message, and first few lines of stack trace
      const stackLines = error.stack?.split('\n').slice(0, 3).join('|') || '';
      return `${error.name}:${error.message}:${stackLines}`;
    }

    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object') {
      try {
        return JSON.stringify(error);
      } catch {
        return String(error);
      }
    }

    return String(error);
  }
}
